import * as React from "react";
import { I18n } from "react-redux-i18n";
import range from "lodash/range";

import { HttpErrorDto } from "@common/dto/http";
import { GetInvitesFilterDto, InviteStatusFilterWithinDate } from "@modules/invite-status/services/invites";
import { InviteFilterInvitesStatus } from "@common/dto/invite/status";

import { InvitesFilterProps } from "./invites-filter.types";

type MultiselectOption = { value: string | number; label: string };
type WithinDropdownOption = { value: InviteStatusFilterWithinDate; label: string };

type MultiselectFilterConfig = {
  id: "questionnaires" | "inviteStatuses" | "invitedBy";
  label: string;
  options: Array<MultiselectOption>;
  onChange: (value: Array<MultiselectOption["value"]>) => void;
  value?: Array<MultiselectOption["value"]>;
};

type WithinFilterConfig = {
  id: "dateSent" | "dateStarted" | "dateCompleted";
  label: string;
  options: Array<WithinDropdownOption>;
  onChange: (value: WithinDropdownOption["value"]) => void;
  value?: WithinDropdownOption["value"];
};

export const useInviteFilterController = (props: InvitesFilterProps) => {
  const { filter, onFilterChange, showErrorMessage, getInvitesFilter } = props;

  const [invitesFilter, setInvitesFilter] = React.useState<GetInvitesFilterDto | undefined>();

  React.useEffect(() => {
    initialize();
  }, []);

  const initialize = () => {
    handleGetInvitesFilter();
  };

  const handleQuestionnaireChange = React.useCallback(
    (value: Array<MultiselectOption["value"]>) => {
      const questionnaires = invitesFilter?.questionnaires.filter((questionnaire) => value.includes(questionnaire.id));
      onFilterChange({ ...filter, questionnaires });
    },
    [filter, onFilterChange, invitesFilter?.questionnaires]
  );

  const handleInvitedStatusChange = React.useCallback(
    (value: Array<MultiselectOption["value"]>) => {
      onFilterChange({ ...filter, inviteStatuses: value as unknown as Array<InviteFilterInvitesStatus> });
    },
    [filter, onFilterChange]
  );

  const handleInvitedByChange = React.useCallback(
    (value: Array<MultiselectOption["value"]>) => {
      onFilterChange({ ...filter, invitedBy: value as string[] });
    },
    [filter, onFilterChange]
  );

  const handleDateSentChange = React.useCallback(
    (value: InviteStatusFilterWithinDate) => {
      onFilterChange({ ...filter, dateSent: value });
    },
    [filter, onFilterChange]
  );

  const handleDateStartedChange = React.useCallback(
    (value: InviteStatusFilterWithinDate) => {
      onFilterChange({ ...filter, dateStarted: value });
    },
    [filter, onFilterChange]
  );

  const handleDateCompletedChange = React.useCallback(
    (value: InviteStatusFilterWithinDate) => {
      onFilterChange({ ...filter, dateCompleted: value });
    },
    [filter, onFilterChange]
  );

  const handleGetInvitesFilter = React.useCallback(async () => {
    try {
      const result = await getInvitesFilter();
      setInvitesFilter(result);
    } catch (err) {
      console.error(err);

      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to get invites filter");
      showErrorMessage(errorMessage);
    }
  }, [showErrorMessage, getInvitesFilter]);

  const withinOptions: Array<WithinDropdownOption> = React.useMemo(() => {
    const optionsRangeIndex = {
      days: { start: 1, end: 5 },
      weeks: { start: 1, end: 4 },
      additionalDays: { start: 1, end: 7 },
    };

    const getDayPrefix = (day: number): InviteStatusFilterWithinDate => `DAYS_${day}`;

    const getWeekPrefix = (day: number): InviteStatusFilterWithinDate => `WEEKS_${day}`;

    return [
      ...range(optionsRangeIndex.days.start, optionsRangeIndex.days.end)
        .reverse()
        .map((day) => ({ value: getDayPrefix(day * 30), label: `${day * 30} ${I18n.t("Days")}` })),
      ...range(optionsRangeIndex.weeks.start, optionsRangeIndex.weeks.end)
        .reverse()
        .map((day) => ({ value: getWeekPrefix(day), label: `${day} ${I18n.t("Weeks")}` })),
      ...range(optionsRangeIndex.weeks.start, optionsRangeIndex.additionalDays.end)
        .reverse()
        .map((day) => ({ value: getDayPrefix(day), label: `${day} ${I18n.t("Days")}` })),
    ];
  }, []);

  const multiselectFilterConfig: Array<MultiselectFilterConfig> = [
    {
      id: "questionnaires",
      label: I18n.t("Select Questionnaire"),
      options:
        invitesFilter?.questionnaires.map((questionnaire) => ({
          value: questionnaire.id,
          label: questionnaire.name,
        })) || [],
      onChange: handleQuestionnaireChange,
      value: filter.questionnaires?.map((q) => q.id),
    },
    {
      id: "inviteStatuses",
      label: I18n.t("Invite Status"),
      options:
        invitesFilter?.inviteStatuses?.map((status) => ({
          value: status,
          label: I18n.t(`invite_${status}`),
        })) || [],
      onChange: handleInvitedStatusChange,
      value: filter.inviteStatuses,
    },
    {
      id: "invitedBy",
      label: I18n.t("Invited By"),
      options: invitesFilter?.invitedBy.map((invited) => ({ value: invited, label: invited })) || [],
      onChange: handleInvitedByChange,
      value: filter.invitedBy,
    },
  ];

  const withinFilterConfig: Array<WithinFilterConfig> = [
    {
      id: "dateSent",
      label: I18n.t("Invited Within"),
      options: withinOptions.map((option) => ({ value: option.value, label: option.label })),
      onChange: handleDateSentChange,
      value: filter.dateSent,
    },
    {
      id: "dateStarted",
      label: I18n.t("Started Within"),
      options: withinOptions.map((option) => ({ value: option.value, label: option.label })),
      onChange: handleDateStartedChange,
      value: filter.dateStarted,
    },
    {
      id: "dateCompleted",
      label: I18n.t("Completed Within"),
      options: withinOptions.map((option) => ({ value: option.value, label: option.label })),
      onChange: handleDateCompletedChange,
      value: filter.dateCompleted,
    },
  ];

  return {
    multiselectFilterConfig,
    withinFilterConfig,
  };
};
